import { callApiSecure, httpMethods } from '@caplend/cognito-auth';
import Cognito from '../../auth/cognito';
import getAccessToken from '../../auth/amplifyAuth';
import transactionSchema from '../../utils/transactionSchema';

const fxApiUrl = process.env.REACT_APP_FX_API_URL;
const externalsApiUrl = process.env.REACT_APP_EXTERNALS_API_URL;
const aiAgentsApiUrl = process.env.REACT_APP_AI_WORKFLOWS_API_URL;

function throwFetchError(response, responseText) {
  const trimmed = (responseText || '').trim();
  let message = trimmed;
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    message = trimmed.slice(1, -1);
  }
  const err = new Error(message || response.statusText || 'Bad Request');
  err.response = {
    status: response.status,
    statusText: response.statusText,
    data: trimmed || message,
  };
  err.status = response.status;
  err.statusCode = response.status;
  err.detail = message;
  throw err;
}

const uploadDocument = async (formData, documentType) => {
  const accessToken = await getAccessToken();
  const response = await fetch(`${fxApiUrl}documents/${documentType}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = responseText ? JSON.parse(responseText) : {};
    } catch {
      errorData = { message: responseText?.trim() || response.statusText };
    }
    const err = new Error(errorData.message || errorData.detail || response.statusText);
    err.response = { status: response.status, statusText: response.statusText, data: responseText };
    err.detail = errorData.detail || (responseText?.trim() && !errorData.message ? responseText.trim() : errorData.message);
    throw err;
  }

  const responseText = await response.text();
  if (responseText) {
    try {
      return JSON.parse(responseText);
    } catch {
      return { success: true, data: responseText };
    }
  }
  return { success: true };
};

const getDocuments = async (documentType) => callApiSecure(`${fxApiUrl}documents/${documentType}`, httpMethods.get, Cognito);

const deleteDocument = async (documentType, fileName) =>
  callApiSecure(`${fxApiUrl}documents/${documentType}/${encodeURIComponent(fileName)}`, httpMethods.delete, Cognito);

const getCompanyPersonDocuments = async (companyPersonId) =>
  callApiSecure(`${fxApiUrl}documents/company-person/${encodeURIComponent(companyPersonId)}`, httpMethods.get, Cognito);

const deleteCompanyPersonDocument = async (companyPersonId, fileName) =>
  callApiSecure(
    `${fxApiUrl}documents/company-person/${encodeURIComponent(companyPersonId)}/${encodeURIComponent(fileName)}`,
    httpMethods.delete,
    Cognito,
  );

const uploadCompanyPersonDocument = async (companyPersonId, formData) => {
  const accessToken = await getAccessToken();
  const response = await fetch(`${fxApiUrl}documents/company-person/${encodeURIComponent(companyPersonId)}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = responseText ? JSON.parse(responseText) : {};
    } catch {
      errorData = { message: responseText?.trim() || response.statusText };
    }
    const err = new Error(errorData.message || errorData.detail || response.statusText);
    err.response = { status: response.status, statusText: response.statusText, data: responseText };
    err.detail = errorData.detail || (responseText?.trim() && !errorData.message ? responseText.trim() : errorData.message);
    throw err;
  }

  const responseText = await response.text();
  if (responseText) {
    try {
      return JSON.parse(responseText);
    } catch {
      return { success: true, data: responseText };
    }
  }
  return { success: true };
};

const parseDocument = async (file, formData) => {
  const accessToken = await getAccessToken();
  formData.append('document', file);

  const response = await fetch(`${aiAgentsApiUrl}v1/documents/parse`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({ message: response.statusText }));
    const err = new Error(errBody.message || `Parse failed: ${response.status}`);
    err.statusCode = response.status;
    err.response = errBody;
    throw err;
  }

  return response.json();
};

const parseTransactionDocument = async (file) => {
  const formData = new FormData();
  formData.append('schema', JSON.stringify(transactionSchema));
  return parseDocument(file, formData);
};

const getUser = async () => callApiSecure(`${fxApiUrl}users`, httpMethods.get, Cognito);
const getUserConfig = async () => callApiSecure(`${fxApiUrl}users/config`, httpMethods.get, Cognito);

const updateUser = async (data) => {
  const accessToken = await getAccessToken();
  const response = await fetch(`${fxApiUrl}users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const responseText = await response.text();
    const error = new Error(response.statusText || 'Bad Request');
    error.response = {
      status: response.status,
      statusText: response.statusText,
      data: responseText,
    };
    error.status = response.status;
    error.statusCode = response.status;
    throw error;
  }

  return response.json();
};
const updateUserDetails = async (data) => callApiSecure(`${fxApiUrl}users/details`, httpMethods.post, Cognito, data);
const upsertUserOnboardingProfile = async (data) => callApiSecure(`${fxApiUrl}users/onboarding-profile`, httpMethods.post, Cognito, data);
const upsertCompanyOnboardingProfile = async (data) => callApiSecure(`${fxApiUrl}users/company/onboarding-profile`, httpMethods.post, Cognito, data);
const updateUserNames = async (data) => callApiSecure(`${fxApiUrl}users/names`, httpMethods.post, Cognito, data);
const updateCompany = async (data) => callApiSecure(`${fxApiUrl}users/company`, httpMethods.post, Cognito, data);
const updateCompanyAddress = async (data) => callApiSecure(`${fxApiUrl}users/company/address`, httpMethods.post, Cognito, data);
const getCompanyPersons = async () => callApiSecure(`${fxApiUrl}users/company-persons`, httpMethods.get, Cognito);
const upsertCompanyPersons = async (data) => callApiSecure(`${fxApiUrl}users/company-persons`, httpMethods.post, Cognito, data);
const getBankAccount = async () => callApiSecure(`${fxApiUrl}users/on-ramp`, httpMethods.get, Cognito);
const getOnRampDeposits = async (provider = 'Noah') =>
  callApiSecure(`${fxApiUrl}transfers/deposits/${encodeURIComponent(provider)}`, httpMethods.get, Cognito);
const getUserPreferences = async () => callApiSecure(`${fxApiUrl}users/preferences`, httpMethods.get, Cognito);
const upsertUserPreferences = async (data) => callApiSecure(`${fxApiUrl}users/preferences`, httpMethods.post, Cognito, data);
const submitOnboarding = async (data) => callApiSecure(`${fxApiUrl}users/onboarding/${data}`, httpMethods.post, Cognito, undefined, 0);
const getOnboardingStatus = async () => callApiSecure(`${fxApiUrl}onboarding`, httpMethods.get, Cognito);
const setOnboardingPhoneNumber = async (data) => callApiSecure(`${fxApiUrl}onboarding/phone-number`, httpMethods.post, Cognito, data);
const setOnboardingUserType = async (data) => callApiSecure(`${fxApiUrl}onboarding/user-type`, httpMethods.post, Cognito, data);
const getTermsConditions = async () => callApiSecure(`${fxApiUrl}onboarding/terms-conditions`, httpMethods.get, Cognito);
const getTermsConditionsAcceptance = async (version) =>
  callApiSecure(`${fxApiUrl}onboarding/terms-conditions/${encodeURIComponent(version)}`, httpMethods.get, Cognito);
const acceptTermsConditions = async (version, user) =>
  callApiSecure(`${fxApiUrl}onboarding/terms-conditions/${encodeURIComponent(version)}`, httpMethods.post, Cognito, { user });
const getBlindPayTosAcceptance = async () => callApiSecure(`${fxApiUrl}onboarding/blindPay/tos`, httpMethods.get, Cognito);
const generateBlindPayTosUrl = async (idempotencyKey) =>
  callApiSecure(`${fxApiUrl}onboarding/blindPay/tos/${encodeURIComponent(idempotencyKey)}`, httpMethods.get, Cognito);
const acceptBlindPayTos = async (tosId) =>
  callApiSecure(`${fxApiUrl}onboarding/blindPay/tos/${encodeURIComponent(tosId)}`, httpMethods.post, Cognito);

const advanceOnboardingStep = async (step) => callApiSecure(`${fxApiUrl}onboarding/${encodeURIComponent(step)}`, httpMethods.patch, Cognito);
const getKycStatus = async () => callApiSecure(`${fxApiUrl}onboarding/kyc`, httpMethods.get, Cognito);
const requestIndividualKyc = async () => callApiSecure(`${fxApiUrl}onboarding/kyc/request/individual`, httpMethods.post, Cognito);
const requestCompanyPersonKyc = async (companyPersonId) =>
  callApiSecure(`${fxApiUrl}onboarding/kyc/request/company-person/${encodeURIComponent(companyPersonId)}`, httpMethods.post, Cognito);

const getApiKeys = async () => callApiSecure(`${fxApiUrl}api-keys`, httpMethods.get, Cognito);

const addApiKey = async (name) => callApiSecure(`${fxApiUrl}api-keys/${encodeURIComponent(name)}`, httpMethods.post, Cognito);

const deleteApiKey = async (keyName) => callApiSecure(`${fxApiUrl}api-keys/${encodeURIComponent(keyName)}`, httpMethods.delete, Cognito);
const getBalances = async () => callApiSecure(`${fxApiUrl}balances`, httpMethods.get, Cognito);
const addBalances = async (data) => callApiSecure(`${fxApiUrl}balances`, httpMethods.post, Cognito, data);
const getTransactions = async (data) => callApiSecure(`${fxApiUrl}cross-border/history`, httpMethods.get, Cognito, data);
const getTransactionsSummary = async (data) => callApiSecure(`${fxApiUrl}cross-border/summary`, httpMethods.get, Cognito, data);
const createDepositTransaction = async (data) => callApiSecure(`${fxApiUrl}euroe/deposit/${data}`, httpMethods.get, Cognito);
const createWithdrawalTransaction = async (data) => callApiSecure(`${fxApiUrl}euroe/withdraw/${data}`, httpMethods.get, Cognito);
const executeWithdrawalTransaction = async (data) => callApiSecure(`${fxApiUrl}euroe/withdraw`, httpMethods.post, Cognito, data);
const cancelTransaction = async (data) => callApiSecure(`${fxApiUrl}euroe/transaction/${data}`, httpMethods.delete, Cognito);
const addWithdrawalBankAccount = async (data) => callApiSecure(`${fxApiUrl}euroe/withdraw/bank`, httpMethods.post, Cognito, data);
const removeEuroeEndAccount = async (data) => callApiSecure(`${fxApiUrl}euroe/account/${data}`, httpMethods.delete, Cognito);
const callAgentSearch = async (data) => callApiSecure(`${fxApiUrl}search`, httpMethods.post, Cognito, data);
const getRecipients = async () => callApiSecure(`${fxApiUrl}recipients`, httpMethods.get, Cognito);
const getRecipient = async (id) => callApiSecure(`${fxApiUrl}recipients/${id}`, httpMethods.get, Cognito);
const getRecipientRequirements = async (currencyCode, bankCountryCode = null) => {
  if (!bankCountryCode) {
    throw new Error('bankCountryCode is required for recipient requirements');
  }

  const accessToken = await getAccessToken();
  const url = `${fxApiUrl}recipients/schema?currencyCode=${encodeURIComponent(currencyCode)}&bankCountryCode=${encodeURIComponent(bankCountryCode)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const responseText = await response.text().catch(() => '');
  if (!response.ok) {
    throwFetchError(response, responseText);
  }

  let parsedResponse = null;
  if (responseText.trim()) {
    try {
      parsedResponse = JSON.parse(responseText);
    } catch {
      throwFetchError(response, responseText);
    }
  }
  const responseData = parsedResponse;

  if (responseData && responseData.rails && Array.isArray(responseData.rails)) {
    const normalizedRails = responseData.rails.map((railItem) => {
      const normalizedSections =
        railItem.sections && Array.isArray(railItem.sections)
          ? railItem.sections.map((section) => {
              const normalizedFields =
                section.fields && Array.isArray(section.fields)
                  ? section.fields.map((field) => {
                      const normalizedField = { ...field };
                      if (field.data && !field.field) {
                        normalizedField.field = field.data;
                      }
                      if (field.values && Array.isArray(field.values)) {
                        normalizedField.values = field.values.map((value) => {
                          if (typeof value === 'object' && value !== null && value.data !== undefined) {
                            return value;
                          }
                          if (typeof value === 'string') {
                            return { data: value, label: value };
                          }
                          return value;
                        });
                      }
                      return normalizedField;
                    })
                  : section.fields;

              return {
                ...section,
                fields: normalizedFields,
                types: section.types && Array.isArray(section.types) ? section.types.map((t) => t.toLowerCase()) : section.types,
              };
            })
          : railItem.sections;

      return {
        ...railItem,
        sections: normalizedSections,
        types: railItem.types && Array.isArray(railItem.types) ? railItem.types.map((t) => t.toLowerCase()) : railItem.types,
      };
    });
    return { ...responseData, rails: normalizedRails };
  }

  return responseData;
};
const addRecipient = async (currencyCode, recipientType, rail, data) => {
  const accessToken = await getAccessToken();
  const url = `${fxApiUrl}recipients/${encodeURIComponent(currencyCode)}/${encodeURIComponent(recipientType)}/${encodeURIComponent(rail)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data ?? {}),
  });

  const responseText = await response.text().catch(() => '');
  const responseData = (() => {
    const trimmed = (responseText || '').trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return trimmed;
    }
  })();

  if (!response.ok) {
    throwFetchError(response, responseText);
  }

  return responseData;
};
const deleteRecipient = async (offRampProvider, recipientId) =>
  callApiSecure(`${fxApiUrl}recipients/${encodeURIComponent(offRampProvider)}/${encodeURIComponent(recipientId)}`, httpMethods.delete, Cognito);
const updateRecipient = async (offRampProvider, recipientId, data) => {
  const accessToken = await getAccessToken();
  const url = `${fxApiUrl}recipients/${encodeURIComponent(offRampProvider)}/${encodeURIComponent(recipientId)}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data ?? {}),
  });

  const responseText = await response.text().catch(() => '');
  const responseData = (() => {
    const trimmed = (responseText || '').trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return trimmed;
    }
  })();

  if (!response.ok) {
    const title =
      responseData && typeof responseData === 'object'
        ? responseData.title || responseData.message || responseData.error || response.statusText
        : response.statusText;
    const err = new Error(title || 'Bad Request');
    err.response = { status: response.status, data: responseData };
    err.status = response.status;
    err.statusCode = response.status;
    if (responseData && typeof responseData === 'object') {
      err.detail = responseData.detail || responseData.message || responseData.error;
      err.traceId = responseData.traceId || responseData.trace_id;
      err.requestId = responseData.requestId || responseData.request_id;
    }
    throw err;
  }

  return responseData;
};

// Need to check by Alisa.
const getSpotRate = async ({ currencyPair, recipientId } = {}) => {
  const accessToken = await getAccessToken();
  const params = new URLSearchParams();
  if (recipientId) {
    params.set('recipientId', recipientId);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const url = `${fxApiUrl}cross-border/spot-rate/${currencyPair}${query}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const responseText = await response.text().catch(() => '');
    const error = new Error(responseText || response.statusText || 'Failed to get spot rate');
    error.status = response.status;
    error.statusCode = response.status;
    throw error;
  }

  return response.json();
};

const getDocumentAccessUrl = async (documentType, fileName) => {
  const res = await callApiSecure(`${fxApiUrl}documents/${documentType}/${encodeURIComponent(fileName)}`, httpMethods.get, Cognito);
  const doc = Array.isArray(res) ? res[0] : res;
  return doc?.accessUrl ?? doc?.AccessUrl ?? null;
};

const getCrossBorderQuote = async (data) => {
  const accessToken = await getAccessToken();
  const response = await fetch(`${fxApiUrl}cross-border/quote`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });

  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(responseData.title || response.statusText);
    error.response = { status: response.status, data: responseData };
    error.status = response.status;
    error.statusCode = response.status;
    throw error;
  }

  return responseData;
};

const executeCrossBorderQuote = async (data) => callApiSecure(`${fxApiUrl}cross-border/quote/execute`, httpMethods.post, Cognito, data, 0);

const getLatestExchangeRate = async (base, target) => {
  const currencyPair = `${base}${target}`;
  const url = `${externalsApiUrl}exchangerate/spotrate/${currencyPair}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  const rate = await response.json();

  return {
    base,
    target,
    rate,
  };
};

const getHistoricalExchangeRates = async (base, target, startDate, endDate) => {
  const currencyPair = `${base}${target}`;
  let url = `${externalsApiUrl}exchangerate/timeseries/${currencyPair}`;

  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

const getIndustryNaces = async () => {
  const response = await fetch(`${externalsApiUrl}reference/industry-naces`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const error = new Error(text || response.statusText || 'Failed to load industry NACE codes');
    error.status = response.status;
    throw error;
  }

  return response.json();
};

const getOccupations = async () => {
  const response = await fetch(`${externalsApiUrl}reference/occupations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const error = new Error(text || response.statusText || 'Failed to load occupations');
    error.status = response.status;
    throw error;
  }

  return response.json();
};

const getReferenceCountries = async () => {
  const response = await fetch(`${externalsApiUrl}reference/countries`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const error = new Error(text || response.statusText || 'Failed to load countries');
    error.status = response.status;
    throw error;
  }

  return response.json();
};

const companySearch = async (body) => {
  const response = await fetch(`${externalsApiUrl}company/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = responseText ? JSON.parse(responseText) : {};
    } catch {
      errorData = { message: responseText?.trim() || response.statusText };
    }
    const err = new Error(errorData.message || errorData.detail || response.statusText);
    err.detail = errorData.detail || errorData.message;
    throw err;
  }

  return response.json();
};

const companyLookup = async (body) => {
  const response = await fetch(`${externalsApiUrl}company/lookup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = responseText ? JSON.parse(responseText) : {};
    } catch {
      errorData = { message: responseText?.trim() || response.statusText };
    }
    const err = new Error(errorData.message || errorData.detail || response.statusText);
    err.detail = errorData.detail || errorData.message;
    throw err;
  }

  return response.json();
};

const companyRepresentatives = async (body) => {
  const response = await fetch(`${externalsApiUrl}company/representatives`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorData;
    try {
      errorData = responseText ? JSON.parse(responseText) : {};
    } catch {
      errorData = { message: responseText?.trim() || response.statusText };
    }
    const err = new Error(errorData.message || errorData.detail || response.statusText);
    err.detail = errorData.detail || errorData.message;
    throw err;
  }

  return response.json();
};

export {
  getDocuments,
  deleteDocument,
  getCompanyPersonDocuments,
  deleteCompanyPersonDocument,
  getUser,
  getUserConfig,
  updateUser,
  updateUserDetails,
  upsertUserOnboardingProfile,
  upsertCompanyOnboardingProfile,
  updateUserNames,
  getBankAccount,
  getOnRampDeposits,
  getUserPreferences,
  upsertUserPreferences,
  updateCompany,
  updateCompanyAddress,
  getCompanyPersons,
  upsertCompanyPersons,
  submitOnboarding,
  getOnboardingStatus,
  setOnboardingPhoneNumber,
  setOnboardingUserType,
  getTermsConditions,
  getTermsConditionsAcceptance,
  acceptTermsConditions,
  getBlindPayTosAcceptance,
  generateBlindPayTosUrl,
  acceptBlindPayTos,
  advanceOnboardingStep,
  getKycStatus,
  requestIndividualKyc as requestKyc,
  requestCompanyPersonKyc,
  getApiKeys,
  addApiKey,
  deleteApiKey,
  getBalances,
  addBalances,
  getTransactions,
  getTransactionsSummary,
  createDepositTransaction,
  createWithdrawalTransaction,
  executeWithdrawalTransaction,
  cancelTransaction,
  addWithdrawalBankAccount,
  removeEuroeEndAccount,
  callAgentSearch,
  getRecipients,
  getRecipient,
  getRecipientRequirements,
  addRecipient,
  deleteRecipient,
  updateRecipient,
  getSpotRate,
  getDocumentAccessUrl,
  uploadDocument,
  uploadCompanyPersonDocument,
  parseTransactionDocument,
  parseDocument,
  getCrossBorderQuote,
  executeCrossBorderQuote,
  getLatestExchangeRate,
  getHistoricalExchangeRates,
  companySearch,
  companyLookup,
  companyRepresentatives,
  getIndustryNaces,
  getOccupations,
  getReferenceCountries,
};
